import { useState, useEffect } from 'react';
import axios from 'axios';
import { Layout } from '../components/Layout';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '../components/ui/dialog';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Trash, CheckCircle, XCircle, Clock, User, Phone, EnvelopeSimple, Barbell } from '@phosphor-icons/react';
import { toast } from 'sonner';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const statusOptions = [
  { value: 'pendente', label: 'Pendente', color: 'badge-warning' },
  { value: 'confirmada', label: 'Confirmada', color: 'badge-info' },
  { value: 'realizada', label: 'Realizada', color: 'badge-success' },
  { value: 'cancelada', label: 'Cancelada', color: 'badge-error' }
];

export default function TrialClassAdmin() {
  const [trials, setTrials] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState('all');
  const [editDialogOpen, setEditDialogOpen] = useState(false);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [currentTrial, setCurrentTrial] = useState(null);
  const [editForm, setEditForm] = useState({ status: '', assigned_date: '', assigned_time: '', notes: '' });
  const [saving, setSaving] = useState(false);

  const fetchTrials = async () => {
    try {
      const { data } = await axios.get(`${API}/trial-classes`, { withCredentials: true });
      setTrials(data);
    } catch (e) {
      toast.error('Erro ao carregar aulas experimentais');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchTrials();
  }, []);

  const handleEdit = (trial) => {
    setCurrentTrial(trial);
    setEditForm({
      status: trial.status,
      assigned_date: trial.assigned_date || trial.preferred_date || '',
      assigned_time: trial.assigned_time || trial.preferred_time || '',
      notes: trial.notes || ''
    });
    setEditDialogOpen(true);
  };

  const handleSubmitEdit = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      await axios.put(`${API}/trial-classes/${currentTrial.id}`, editForm, { withCredentials: true });
      toast.success('Aula experimental atualizada');
      setEditDialogOpen(false);
      fetchTrials();
    } catch (e) {
      toast.error('Erro ao atualizar');
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async () => {
    try {
      await axios.delete(`${API}/trial-classes/${currentTrial.id}`, { withCredentials: true });
      toast.success('Solicitação removida');
      setDeleteDialogOpen(false);
      fetchTrials();
    } catch (e) {
      toast.error('Erro ao remover');
    }
  };

  const filteredTrials = filter === 'all' ? trials : trials.filter(t => t.status === filter);
  const pendingCount = trials.filter(t => t.status === 'pendente').length;

  const getStatusBadge = (status) => {
    const opt = statusOptions.find(o => o.value === status);
    return opt ? opt.color : 'badge-info';
  };

  return (
    <Layout>
      <div className="space-y-6 animate-fade-in" data-testid="trial-admin-page">
        {/* Header */}
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
          <div>
            <h1 className="page-title">Aulas Experimentais</h1>
            <p className="text-[#73706A] mt-1">
              Gerenciar solicitações de aulas experimentais
              {pendingCount > 0 && (
                <span className="ml-2 badge badge-warning">{pendingCount} pendente(s)</span>
              )}
            </p>
          </div>
          <div className="flex items-center gap-2">
            <span className="text-sm text-[#73706A]">Filtrar:</span>
            <Select value={filter} onValueChange={setFilter}>
              <SelectTrigger className="w-40" data-testid="trial-filter-select">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">Todas</SelectItem>
                {statusOptions.map(opt => (
                  <SelectItem key={opt.value} value={opt.value}>{opt.label}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
        </div>

        {/* Stats */}
        <div className="grid grid-cols-2 sm:grid-cols-4 gap-4">
          {statusOptions.map(opt => {
            const count = trials.filter(t => t.status === opt.value).length;
            return (
              <div key={opt.value} className="stat-card text-center cursor-pointer" onClick={() => setFilter(opt.value)}>
                <p className="text-2xl font-bold font-['Outfit'] text-[#1A1A1A]">{count}</p>
                <p className="text-sm text-[#73706A] capitalize">{opt.label}</p>
              </div>
            );
          })}
        </div>

        {/* List */}
        <div className="space-y-4">
          {loading ? (
            <div className="bg-white border border-[#E6E2D8] rounded-lg p-8 text-center">
              <div className="w-8 h-8 border-4 border-[#E85D04] border-t-transparent rounded-full animate-spin mx-auto" />
            </div>
          ) : filteredTrials.length === 0 ? (
            <div className="bg-white border border-[#E6E2D8] rounded-lg p-8 text-center">
              <Barbell size={48} className="text-[#E6E2D8] mx-auto mb-4" />
              <p className="text-[#73706A]">Nenhuma solicitação encontrada</p>
            </div>
          ) : (
            filteredTrials.map((trial, index) => (
              <div
                key={trial.id}
                className="bg-white border border-[#E6E2D8] rounded-lg p-5 hover:border-[#E85D04] transition-colors"
                data-testid={`trial-card-${index}`}
              >
                <div className="flex items-start justify-between">
                  <div className="flex-1">
                    <div className="flex items-center gap-3 mb-3">
                      <div className="w-10 h-10 bg-[#E85D04] rounded-full flex items-center justify-center">
                        <User size={18} className="text-white" />
                      </div>
                      <div>
                        <h3 className="font-medium text-[#1A1A1A]">{trial.name}</h3>
                        <span className={`badge ${getStatusBadge(trial.status)} capitalize`}>{trial.status}</span>
                      </div>
                    </div>
                    <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 text-sm">
                      <div className="flex items-center gap-2 text-[#73706A]">
                        <EnvelopeSimple size={16} />
                        <span>{trial.email}</span>
                      </div>
                      <div className="flex items-center gap-2 text-[#73706A]">
                        <Phone size={16} />
                        <span>{trial.phone}</span>
                      </div>
                      <div className="flex items-center gap-2 text-[#0077B6]">
                        <Clock size={16} />
                        <span>{trial.preferred_date} {trial.preferred_time}</span>
                      </div>
                      <div className="flex items-center gap-2 text-[#73706A]">
                        <Barbell size={16} />
                        <span className="capitalize">{trial.experience_level}</span>
                      </div>
                    </div>
                    {trial.notes && (
                      <p className="text-sm text-[#73706A] mt-2 italic">"{trial.notes}"</p>
                    )}
                  </div>
                  <div className="flex items-center gap-2 ml-4">
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => handleEdit(trial)}
                      data-testid={`trial-edit-${index}`}
                    >
                      Gerenciar
                    </Button>
                    <Button
                      variant="ghost"
                      size="sm"
                      onClick={() => { setCurrentTrial(trial); setDeleteDialogOpen(true); }}
                      className="text-[#9B2226] hover:bg-red-50"
                      data-testid={`trial-delete-${index}`}
                    >
                      <Trash size={18} />
                    </Button>
                  </div>
                </div>
              </div>
            ))
          )}
        </div>
      </div>

      {/* Edit Dialog */}
      <Dialog open={editDialogOpen} onOpenChange={setEditDialogOpen}>
        <DialogContent className="sm:max-w-[450px]">
          <DialogHeader>
            <DialogTitle className="font-['Outfit'] text-xl">Gerenciar Aula Experimental</DialogTitle>
          </DialogHeader>
          <form onSubmit={handleSubmitEdit} className="space-y-4 mt-4">
            <div>
              <Label>Status</Label>
              <Select value={editForm.status} onValueChange={(v) => setEditForm({ ...editForm, status: v })}>
                <SelectTrigger data-testid="trial-edit-status">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {statusOptions.map(opt => (
                    <SelectItem key={opt.value} value={opt.value}>{opt.label}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label>Data Confirmada</Label>
                <Input
                  type="date"
                  value={editForm.assigned_date}
                  onChange={(e) => setEditForm({ ...editForm, assigned_date: e.target.value })}
                  data-testid="trial-edit-date"
                />
              </div>
              <div>
                <Label>Horário Confirmado</Label>
                <Input
                  type="time"
                  value={editForm.assigned_time}
                  onChange={(e) => setEditForm({ ...editForm, assigned_time: e.target.value })}
                  data-testid="trial-edit-time"
                />
              </div>
            </div>
            <div>
              <Label>Observações</Label>
              <textarea
                value={editForm.notes}
                onChange={(e) => setEditForm({ ...editForm, notes: e.target.value })}
                className="input-field min-h-[80px] resize-none"
                data-testid="trial-edit-notes"
              />
            </div>
            <DialogFooter>
              <Button type="button" variant="outline" onClick={() => setEditDialogOpen(false)}>Cancelar</Button>
              <Button type="submit" disabled={saving} className="bg-[#E85D04] hover:bg-[#DC2F02]" data-testid="trial-edit-save">
                {saving ? 'Salvando...' : 'Salvar'}
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>

      {/* Delete Dialog */}
      <Dialog open={deleteDialogOpen} onOpenChange={setDeleteDialogOpen}>
        <DialogContent className="sm:max-w-[400px]">
          <DialogHeader>
            <DialogTitle className="font-['Outfit'] text-xl">Confirmar exclusão</DialogTitle>
          </DialogHeader>
          <p className="text-[#73706A] mt-2">
            Remover solicitação de <strong>{currentTrial?.name}</strong>?
          </p>
          <DialogFooter className="mt-4">
            <Button variant="outline" onClick={() => setDeleteDialogOpen(false)}>Cancelar</Button>
            <Button onClick={handleDelete} className="bg-[#9B2226] hover:bg-[#7d1c1f]" data-testid="trial-confirm-delete">
              Remover
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </Layout>
  );
}
